import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { HeartHandshake, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {
        setStatus("error");
        return;
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-lg text-center">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="animate-spin text-[#8A5A44] mx-auto" />
            <p className="mt-6 text-[#5C5854] font-light" data-testid="payment-checking">
              Confirming your gift…
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <HeartHandshake size={44} strokeWidth={1.25} className="text-[#8A5A44] mx-auto" />
            <h1 className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#2C2A28] mt-8" data-testid="payment-confirmed">
              Thank you for <em className="italic text-[#8A5A44]">giving.</em>
            </h1>
            <p className="mt-6 text-[#5C5854] font-light leading-relaxed">
              Your gift keeps this ministry free for every couple who reaches out — including those
              who could never afford help otherwise. The Wicks are grateful for you.
            </p>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#2C2A28]" data-testid="payment-issue">
              We couldn't confirm the payment yet.
            </h1>
            <p className="mt-6 text-[#5C5854] font-light leading-relaxed">
              If you completed checkout, your gift is safe — confirmation may just be delayed.
              Reach out to us if anything looks off.
            </p>
          </>
        )}
        <Link
          to="/"
          className="inline-block mt-10 rounded-full bg-[#8A5A44] hover:bg-[#734A38] text-[#FAF9F6] px-8 py-3.5 text-sm transition-colors duration-300"
          data-testid="payment-back-home"
        >
          Back to the ministry
        </Link>
      </div>
    </div>
  );
}
